import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { flattenSearchGroups, orderSearchGroups, searchDestination } from "@/lib/search/search-session";
import { useSearch, type SearchEntryType, type SearchIndexEntry } from "./useSearch";

export type SearchSessionStatus = "idle" | "loading" | "results" | "empty" | "error";

const DEBOUNCE_MS = 250;

/**
 * 探す窓 1 回ぶんの状態。入力、250ms の待ち、口 (/api/luagate-search) の呼び出し、キーボードの選び、行き先への移動を持つ。
 * 古い窓 (GlobalSearch) と /luagate の窓 (LgSearchWindow) が同じものを使い、探し方を 2 重に書かない。
 * byRank は 1 列に関連の順で並べる窓だけが付ける (古い窓は種類のまとまりで見せるので付けない)。
 * groupOrder は窓ごとのまとまりの順 (既定は search-session の SEARCH_GROUP_ORDER)。公開の頁の窓だけ別の順を渡す
 */
export function useSearchSession({
  isOpen = true,
  isTrial = false,
  byRank = false,
  groupOrder,
  onClose,
}: {
  isOpen?: boolean;
  isTrial?: boolean;
  byRank?: boolean;
  groupOrder?: readonly SearchEntryType[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { search, clear, groups, isLoading, error } = useSearch();
  const [query, setQuery] = useState("");
  const [rawIndex, setSelectedIndex] = useState(0);
  // 返事が届いた (成功も失敗も) 最後の問い。入力と食い違う間は「探している最中」
  const [settled, setSettled] = useState("");
  const [attempt, setAttempt] = useState(0);

  const orderedGroups = useMemo(() => orderSearchGroups(groups, groupOrder), [groups, groupOrder]);
  const items = useMemo(() => flattenSearchGroups(groups, { byRank, order: groupOrder }), [groups, byRank, groupOrder]);
  const trimmed = query.trim();

  useEffect(() => {
    if (!trimmed) {
      clear();
      setSettled("");
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void search(query).then(() => {
        if (!cancelled) setSettled(trimmed);
      });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // query が変わったときと「もう一度」のときだけ。search と clear は安定している
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, attempt]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const selectedIndex = items.length === 0 ? 0 : Math.min(rawIndex, items.length - 1);
  const searching = trimmed !== "" && (settled !== trimmed || isLoading);

  const status: SearchSessionStatus = !trimmed
    ? "idle"
    : items.length > 0
      ? "results"
      : searching
        ? "loading"
        : error
          ? "error"
          : "empty";

  const go = useCallback(
    (item: SearchIndexEntry) => {
      const url = searchDestination(item, isTrial);
      if (!url) return;
      navigate(url);
      onClose();
    },
    [navigate, onClose, isTrial],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.nativeEvent.isComposing) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(Math.min(selectedIndex + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      } else if (e.key === "Enter" && items[selectedIndex]) {
        e.preventDefault();
        go(items[selectedIndex]);
      }
    },
    [items, selectedIndex, go],
  );

  const retry = useCallback(() => {
    setSettled("");
    setAttempt((n) => n + 1);
  }, []);

  return { query, setQuery, orderedGroups, items, selectedIndex, setSelectedIndex, status, searching, error, onKeyDown, go, retry };
}
